import React from 'react';
import SnapshotImg from '../SnapshotImg';
import { getCfHeaders, snapshotUrl, WIDE_ASPECT } from '../../utils/cameraRelay';
import { C, KIND_DOT } from './CamerasStyles';
import { DoorChips } from './DoorAlarm';
import { doorChipsAlarm } from '../../features/security/doors';

/** A camera's health row from /health → a short problem label, or null when fine. */
export function cameraProblem(h) {
  if (!h) return null;
  if (h.stream === 'missing') return 'No feed';
  if (h.stream === 'stale') return h.stream_age_sec > 0 ? `Feed frozen · ${Math.round(h.stream_age_sec)} s` : 'Feed frozen';
  if (h.recording === 'no_files') return 'Not recording';
  if (h.recording === 'stale') return 'Recording stalled';
  return null;
}

export function healthFor(health, camera) {
  const rows = health?.cameras?.[camera.station];
  return Array.isArray(rows) ? rows.find((r) => r.index === camera.index) || null : null;
}

/** The second caption line: what JobQ says this station is doing. */
export function ActivityLine({ camera, activity, compact = false }) {
  if (!camera.jobqStation) {
    return <span style={{ fontSize: 13, color: C.faint }}>Not a JobQ station</span>;
  }
  if (!activity) {
    return <span style={{ fontSize: 13, color: C.faint }}>&nbsp;</span>;
  }
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: compact ? 12 : 13, color: C.textSoft, minWidth: 0 }}>
      <span className="jqc-dot" style={{ background: KIND_DOT[activity.kind] || '#5f6771' }} />
      <span className="jqc-ell" style={{ flex: 1 }}>{activity.text}</span>
      {activity.meta && !compact && (
        <span style={{ color: C.muted, fontSize: 12, whiteSpace: 'nowrap' }}>{activity.meta}</span>
      )}
    </span>
  );
}

export function LiveBadge({ problem }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: problem ? C.warn : C.muted, fontSize: 12, whiteSpace: 'nowrap' }}>
      <span className="jqc-dot" style={{ background: problem ? C.warn : C.live }} />
      {problem ? 'Problem' : 'Live'}
    </span>
  );
}

const WATCH_TAG = {
  watched: { color: '#cfe3ff', text: 'Watched' },
  ignored: { color: C.muted, text: null },
  snoozed: { color: C.warn, text: null },
};
const ALERT_TONE = { bad: '#ffb0b0', warn: '#ffd38a' };

/** Night watch on a tile (features/security tileWatch): "Watched" / "Not watched at night" / "Snoozed…". */
export function WatchTag({ watch }) {
  if (!watch) return null;
  const t = WATCH_TAG[watch.kind] || WATCH_TAG.ignored;
  return (
    <span data-testid="watch-tag"
          style={{ position: 'absolute', right: 8, top: 8, display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 600,
                   padding: '2px 8px', borderRadius: 999, background: 'rgba(10,12,14,.78)', color: t.color }}>
      {watch.kind === 'watched' && (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
          <path d="M12 3l7 3v6c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6z" />
        </svg>
      )}
      {t.text || watch.label}
    </span>
  );
}

/** The caption line for 12 h after an alert (features/security tileAlert). */
export function AlertLine({ lastAlert }) {
  return (
    <span className="jqc-ell" style={{ fontSize: 13, color: ALERT_TONE[lastAlert.tone] || C.textSoft }} data-testid="tile-alert">
      ● {lastAlert.text}
    </span>
  );
}

/** A red frame while one of the tile's doors is open or holding an alert (features/security/doors). */
export const DOOR_ALARM_FRAME = { borderColor: C.bad, boxShadow: `0 0 0 2px ${C.bad} inset` };

export default function CameraTile({ camera, aspect, activity, health, onOpen, onFrameSize, intervalMs = 1500, watch = null, lastAlert = null, doorChips = null }) {
  const wide = aspect > WIDE_ASPECT;
  const problem = cameraProblem(health);
  const alerting = lastAlert && (lastAlert.tone === 'bad' || lastAlert.tone === 'warn');
  const doorAlarm = doorChipsAlarm(doorChips);
  return (
    <button
      type="button"
      className={`jqc-tile${wide ? ' jqc-wide' : ''}`}
      onClick={() => onOpen(camera)}
      aria-label={`Watch ${camera.label} live`}
      data-door-alarm={doorAlarm ? 'true' : undefined}
      style={doorAlarm ? DOOR_ALARM_FRAME : alerting ? { borderColor: '#7a2a2a', boxShadow: '0 0 0 1px #7a2a2a inset' } : undefined}
    >
      <span className={`jqc-frame ${wide ? 'jqc-r-wide' : 'jqc-r-std'}`}>
        <SnapshotImg
          src={snapshotUrl(camera.station, camera.index)}
          alt=""
          authHeaders={getCfHeaders()}
          intervalMs={intervalMs}
          onFrameSize={onFrameSize ? (w, h) => onFrameSize(camera.key, w, h) : undefined}
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
        {problem && (
          <span style={{ position: 'absolute', top: 8, left: 8, padding: '3px 9px', borderRadius: 999, background: 'rgba(20,14,4,.85)', color: C.warn, fontSize: 12, fontWeight: 600 }}>
            {problem}
          </span>
        )}
        <WatchTag watch={watch} />
      </span>
      <span style={{ display: 'flex', flexDirection: 'column', gap: 5, padding: '10px 12px 12px', minWidth: 0 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <span style={{ fontWeight: 600, fontSize: 15 }}>{camera.station}</span>
          {camera.sub && <span style={{ color: C.muted, fontSize: 13 }}>{camera.sub}</span>}
          <span style={{ flex: 1 }} />
          <LiveBadge problem={problem} />
        </span>
        {lastAlert ? <AlertLine lastAlert={lastAlert} /> : <ActivityLine camera={camera} activity={activity} />}
        <DoorChips chips={doorChips} />
      </span>
    </button>
  );
}
